import React, { useCallback, useEffect, useState } from "react";

const LOCAL_DEV_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);
const VPN_PROBE_PATH = "/__pw_api/Api/Config/appFlags";

// This is a local-development DX guard, not a security boundary. Backend VPN
// controls remain authoritative. Never enable this in a production build: it
// checks backend reachability only, never attempts to infer VPN state.
export function isLocalDevVpnGate({
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
  nodeEnv = process.env.NODE_ENV,
} = {}) {
  return nodeEnv === "development" && LOCAL_DEV_HOSTS.has(String(hostname || "").toLowerCase());
}

export async function probeVpnBackend({ fetchImpl = fetch, timeoutMs = 5000 } = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(VPN_PROBE_PATH, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function GateScreen({ checking, onRetry }) {
  return (
    <main
      role="main"
      aria-live="polite"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#0A0A14",
        color: "#F8FAFC",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <section style={{ maxWidth: 460, textAlign: "center" }}>
        <div style={{ fontSize: 44 }} aria-hidden="true">⚠️</div>
        <h1 style={{ margin: "16px 0 10px", fontSize: 24 }}>Connect to VPN to continue</h1>
        <p style={{ margin: 0, color: "#CBD5E1", lineHeight: 1.55 }}>
          Connect to the company VPN (Pritunl) to use the local environment.
        </p>
        <p style={{ minHeight: 22, margin: "12px 0 20px", color: "#94A3B8", fontSize: 14 }}>
          {checking ? "Checking the VPN backend…" : "Then retry this connection."}
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={checking}
          style={{
            border: 0,
            borderRadius: 10,
            padding: "11px 18px",
            background: "#8B5CF6",
            color: "#FFFFFF",
            fontWeight: 700,
            cursor: checking ? "default" : "pointer",
            opacity: checking ? 0.7 : 1,
          }}
        >
          Retry
        </button>
      </section>
    </main>
  );
}

export default function VpnGate({ children, enabled = isLocalDevVpnGate(), probe = probeVpnBackend }) {
  const [state, setState] = useState(enabled ? "checking" : "ready");

  const checkReachability = useCallback(async () => {
    if (!enabled) return;
    setState("checking");
    const reachable = await probe();
    setState(reachable ? "ready" : "blocked");
  }, [enabled, probe]);

  useEffect(() => {
    if (enabled) void checkReachability();
  }, [enabled, checkReachability]);

  if (!enabled || state === "ready") return children;
  return <GateScreen checking={state === "checking"} onRetry={checkReachability} />;
}
